import {
  COLOR,
  mq,
  FONT_SIZE,
  FONT_WEIGHT,
  Heading as _Heading,
  ProgressBar as _ProgressBar,
  LazyImage as _LazyImage,
} from "@dmm-com/rocket-web-ui-library";
import styled from "@emotion/styled";
import { MINI_DETAIL_SIZE } from "../mini-detail/const";
import { TRANSITION_TIME } from "~/const/mini-detail";
import { fadeIn } from "~/styles/animation";

import { LINE_STYLE } from "~/styles/line-style";

type RankingKeyObject = {
  [key: number]: string;
};

const rankingColor: RankingKeyObject = {
  1: COLOR.YELLOW50.code,
  2: COLOR.BLUE900.code,
  3: COLOR.RED900.code,
};

export const LazyImageWrapper = styled.div<{
  canHover: boolean;
  shouldShowPreviewModal: boolean;
  width?: string;
}>`
  position: relative;

  ${({ shouldShowPreviewModal, canHover }) =>
    canHover &&
    shouldShowPreviewModal &&
    `
      @media (any-hover: hover) {
        :hover {
          div[data-type="previewModal"] {
            opacity: 1;
            display: block;
          }
        }
        div[data-type="previewModal"] {
          opacity: 1;
          display: block;
        }
      }
    `}
`;

export const LazyImage = styled(_LazyImage)`
  img {
    background: none;
  }
  > picture {
    background: none;
  }
`;

export const DivWrapper = styled.div<{
  width?: string;
}>`
  width: ${({ width }) => width ?? "100%"};
  line-height: 1.6;
  position: relative;
`;

export const DivWrapperImg = styled.div<{
  isRanking?: boolean;
}>`
  position: relative;

  ${({ isRanking }) =>
    isRanking &&
    `
      border-radius: 8px;
      box-shadow: 3px 3px 0px ${COLOR.YELLOW900.code};
      margin-bottom: 16px;
      background-color: ${COLOR.YELLOW900.code};

      ${mq.sp} {
        margin-bottom: 8px;
      }
    `};
`;

export const DivOverlayWrapper = styled.div<{ isOverlayTransparent: boolean; isOverlayClickable: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: ${({ isOverlayTransparent }) =>
    isOverlayTransparent ? `${COLOR.TRANSPARENT.code}` : "rgba(0, 0, 0, 0.4)"};
  cursor: ${({ isOverlayClickable }) => (isOverlayClickable ? "pointer" : "default")};
`;

export const DivOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: ${COLOR.GRAY1000.code};
  font-size: ${FONT_SIZE.M}px;
  font-weight: ${FONT_WEIGHT.BOLD};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

export const DivLeftTagWrapper = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
`;

export const DivRightTagWrapper = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
`;

export const SpanSupplement = styled.span`
  position: absolute;
  font-size: ${FONT_SIZE.XXS}px;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 0 4px;
  border-radius: 4px;
  display: flex;
  ${mq.sp} {
    transform: translate(10%, 10%) scale(0.8);
    bottom: 4px;
    right: 4px;
  }
`;

export const SpanRankingOrder = styled.span<{
  rankingOrder: number;
}>`
  position: absolute;
  bottom: -3px;
  left: -3px;
  font-family: "Helvetica Neue", Arial, sans-serif;
  font-weight: ${FONT_WEIGHT.BOLD};
  font-size: ${FONT_SIZE.XXL}px;
  letter-spacing: -2px;
  text-shadow: 0px 0px 24px rgba(0, 0, 0, 0.2);
  border-radius: 8px 50px 8px 8px;
  width: 48px;
  height: 48px;
  text-align: center;
  text-indent: -6px;
  display: grid;
  place-items: center;
  line-height: 1;

  background-color: ${({ rankingOrder }) =>
    rankingOrder > 0 && rankingOrder < 4
      ? `${rankingColor[rankingOrder]}`
      : rankingOrder < 31 && `${COLOR.GRAY500.code}`};
  color: ${({ rankingOrder }) =>
    rankingOrder > 0 && rankingOrder < 4
      ? `${COLOR.GRAY1000.code}`
      : rankingOrder < 31 && `${COLOR.GRAY900.code}`};

  ${mq.sp} {
    width: 32px;
    height: 32px;
    font-size: ${FONT_SIZE.LL}px;
  }
`;

export const DivDescription = styled.div`
  position: relative;
  font-size: ${FONT_SIZE.S}px;
  width: 100%;
`;

export const Heading = styled(_Heading)<{ lineClampNum: number }>`
  font-weight: ${FONT_WEIGHT.NORMAL};
  margin-top: 8px;
  word-break: break-all;
  ${({ lineClampNum }) => lineClampNum > 0 && LINE_STYLE(lineClampNum)}

  ${mq.sp} {
    font-size: ${FONT_SIZE.XS}px;
  }
`;

export const ParSubText = styled.p<{ lineClampNum: number }>`
  font-size: ${FONT_SIZE.XS}px;
  color: ${COLOR.GRAY500.code};
  ${({ lineClampNum }) => lineClampNum > 0 && LINE_STYLE(lineClampNum)}
`;

export const DivBottomLeftAdormentContainer = styled.div<{ hasBottomTag: boolean }>`
  display: flex;
  align-items: center;
  margin-top: ${({ hasBottomTag }) => hasBottomTag && "8px"};
  > :not(:first-of-type) {
    margin-left: 8px;
  }
`;

export const DivPriceTextWrapper = styled.div`
  display: flex;
  align-items: center;

  ${mq.sp} {
    font-size: ${FONT_SIZE.XS}px;
  }
`;

export const ProgressBar = styled(_ProgressBar)`
  position: absolute;
  bottom: 0;
  left: 0;
`;

export const DivRatingStarWrapper = styled.div`
  display: flex;
  align-items: center;
  /* MEMO(uchida): デフォルトの「line-height=1.6」と同一の値だけ高さを持つようにする */
  height: 1.6em;
  line-height: 1;
  margin-bottom: 2px;
`;

export const SpanRatingStarScore = styled.span`
  font-size: 12px;
  margin-left: 4px;
`;

export const MouseDevicePreviewModalWrapper = styled.div<{
  isFirstItem?: boolean;
  isLastItem?: boolean;
  isMouseHover?: boolean;
  zIndex: string;
}>`
  width: 100%;
  height: 100%;
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 16px;
  overflow: hidden;
  transform: translate(-50%, -50%);
  animation: ${fadeIn} 0.25s;
  transition: width ${TRANSITION_TIME}s, height ${TRANSITION_TIME}s;

  @media (any-hover: hover) {
    &:hover {
      ${({ isMouseHover }) => isMouseHover && `width: ${MINI_DETAIL_SIZE.BASE}px;`};
      ${({ isMouseHover }) => isMouseHover && `height: ${MINI_DETAIL_SIZE.BASE}px;`};
      ${({ zIndex }) => `z-index: ${zIndex};`};
    }
  }

  ${({ isFirstItem }) =>
    isFirstItem &&
    `
      left: 0%;
      transform: translate(0%, -50%);
    `}

  ${({ isLastItem }) =>
    isLastItem &&
    `
      right: 0%;
      left: auto;
      transform: translate(0%, -50%);
    `}
`;

export const TouchDevicePreviewModalWrapper = styled.div<{
  isFirstItem?: boolean;
  isLastItem?: boolean;
  zIndex: string;
  isShownByTouched: boolean;
  isMouseHover?: boolean;
}>`
  width: ${({ isMouseHover }) => (isMouseHover ? MINI_DETAIL_SIZE.BASE : 0)}px;
  height: ${({ isMouseHover }) => (isMouseHover ? MINI_DETAIL_SIZE.BASE : 0)}px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 16px;
  overflow: hidden;
  display: ${({ isShownByTouched }) => (isShownByTouched ? "block" : "none")};

  ${({ zIndex }) =>
    `
      z-index: ${zIndex};
    `};
  ${({ isFirstItem }) =>
    isFirstItem &&
    `
      left: 0%;
      transform: translate(0%, -50%);
    `}
  ${({ isLastItem }) =>
    isLastItem &&
    `
      right: 0%;
      left: auto;
      transform: translate(0%, -50%);
    `}
`;

export const TouchDevicePreviewModalBody = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  width: ${MINI_DETAIL_SIZE.BASE}px;
  height: ${MINI_DETAIL_SIZE.BASE}px;
  border-radius: 16px;
  background-color: ${COLOR.GRAY200.code};
  cursor: pointer;
`;
